import React, { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../../firebase";
import { AppContext } from "../../../../store/AppContext";
import { CustomButton } from "../../../../components/CustomButton/CustomButton";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Switch,
  Typography,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import "../../coupons.css";

const schema = yup.object().shape({
  code: yup.string().trim().required("Coupon code is required"),
  discountType: yup.string().required("Select a discount type"),
  discountValue: yup
    .number()
    .typeError("Enter a valid amount")
    .min(0, "Must be 0 or more")
    .required("Discount value is required"),
});

const toInputDate = (v) => {
  if (!v) return "";
  try {
    const d = v.toDate ? v.toDate() : new Date(v);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

const AddNewCoupon = () => {
  const { couponTabInfo, setCouponTabInfo } = useContext(AppContext);
  const selectedCoupon = couponTabInfo?.selectedCoupon;

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [saving, setSaving] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: selectedCoupon
      ? {
          code: selectedCoupon.code || "",
          description: selectedCoupon.description || "",
          discountType: selectedCoupon.discountType || "percentage",
          discountValue: selectedCoupon.discountValue ?? "",
          minOrderValue: selectedCoupon.minOrderValue ?? "",
          maxDiscountAmount: selectedCoupon.maxDiscountAmount ?? "",
          applicableProducts: selectedCoupon.applicableProducts || [],
          applicableCategories: selectedCoupon.applicableCategories || [],
          usageLimit: selectedCoupon.usageLimit ?? "",
          perUserLimit: selectedCoupon.perUserLimit ?? "",
          startDate: toInputDate(selectedCoupon.startDate),
          endDate: toInputDate(selectedCoupon.endDate),
          active: selectedCoupon.active !== false,
        }
      : {
          code: "",
          description: "",
          discountType: "percentage",
          discountValue: "",
          minOrderValue: "",
          maxDiscountAmount: "",
          applicableProducts: [],
          applicableCategories: [],
          usageLimit: "",
          perUserLimit: "",
          startDate: "",
          endDate: "",
          active: true,
        },
  });

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [pSnap, cSnap] = await Promise.all([
          getDocs(collection(db, "products")),
          getDocs(collection(db, "categories")),
        ]);
        setProducts(
          pSnap.docs.map((d) => {
            const data = d.data();
            return { id: data.id || d.id, name: data.productName || data.id || d.id };
          })
        );
        setCategories(
          cSnap.docs.map((d) => {
            const data = d.data();
            return { id: data.id || d.id, name: data.categoryName || data.id || d.id };
          })
        );
      } catch (e) {
        console.error("Failed to load products/categories:", e);
      } finally {
        setLoadingMeta(false);
      }
    };
    fetchMeta();
  }, []);

  const goBack = () =>
    setCouponTabInfo({
      addCoupon: false,
      trigger: false,
      selectedCoupon: null,
      page: 1,
      totalPages: 1,
    });

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const code = (data.code || "").trim().toUpperCase();
      const num = (v) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : 0;
      };

      const payload = {
        code,
        codeLower: code.toLowerCase(),
        description: data.description || "",
        discountType: data.discountType || "percentage",
        discountValue: num(data.discountValue),
        minOrderValue: num(data.minOrderValue),
        maxDiscountAmount: num(data.maxDiscountAmount),
        applicableProducts: data.applicableProducts || [],
        applicableCategories: data.applicableCategories || [],
        usageLimit: num(data.usageLimit),
        perUserLimit: num(data.perUserLimit),
        startDate: data.startDate
          ? new Date(data.startDate + "T00:00:00")
          : null,
        endDate: data.endDate ? new Date(data.endDate + "T23:59:59") : null,
        active: data.active !== false,
        updatedDate: new Date(),
      };

      if (selectedCoupon) {
        await updateDoc(doc(db, "coupons", selectedCoupon.id), payload);
      } else {
        const ref = await addDoc(collection(db, "coupons"), {
          ...payload,
          usageCount: 0,
          createdDate: new Date(),
        });
        await updateDoc(ref, { id: ref.id });
      }
      goBack();
    } catch (e) {
      console.error("Failed to save coupon:", e);
      alert("Failed to save coupon: " + (e.message || "unknown error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="coupon-form-page">
      <div className="coupons-panel">
        <div className="coupon-form-back" onClick={goBack} role="button" tabIndex={0}>
          <ArrowBackIcon fontSize="small" /> Back to coupons
        </div>

        <div className="coupons-page-header">
          <h2>{selectedCoupon ? "Edit coupon" : "Add new coupon"}</h2>
        </div>

      {loadingMeta ? (
        <Box display="flex" justifyContent="center" padding="40px">
          <CircularProgress />
        </Box>
      ) : (
        <form className="coupon-form-card" onSubmit={handleSubmit(onSubmit)}>
          <div className="coupon-form-grid">
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Coupon code"
                  placeholder="e.g. SUMMER20"
                  fullWidth
                  size="small"
                  error={!!errors.code}
                  helperText={errors.code?.message}
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                />
              )}
            />

            <Controller
              name="discountType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small">
                  <InputLabel>Discount type</InputLabel>
                  <Select label="Discount type" {...field}>
                    <MenuItem value="percentage">Percentage (%) off</MenuItem>
                    <MenuItem value="flat">Flat amount (₹) off</MenuItem>
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name="discountValue"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Discount value"
                  type="number"
                  fullWidth
                  size="small"
                  error={!!errors.discountValue}
                  helperText={errors.discountValue?.message}
                />
              )}
            />

            <Controller
              name="minOrderValue"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Min order value (₹) — 0 = none"
                  type="number"
                  fullWidth
                  size="small"
                />
              )}
            />

            <Controller
              name="maxDiscountAmount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Max discount (₹) — cap, 0 = none"
                  type="number"
                  fullWidth
                  size="small"
                />
              )}
            />

            <Controller
              name="usageLimit"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Total usage limit — 0 = unlimited"
                  type="number"
                  fullWidth
                  size="small"
                />
              )}
            />

            <Controller
              name="perUserLimit"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Per-user limit — 0 = unlimited"
                  type="number"
                  fullWidth
                  size="small"
                />
              )}
            />

            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Start date"
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />

            <Controller
              name="endDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="End date"
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />

            <Controller
              name="applicableProducts"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small">
                  <InputLabel>Applicable products (empty = all)</InputLabel>
                  <Select
                    multiple
                    label="Applicable products (empty = all)"
                    {...field}
                    value={field.value || []}
                    renderValue={(sel) =>
                      sel.length ? `${sel.length} product(s)` : "All products"
                    }
                  >
                    {products.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name="applicableCategories"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small">
                  <InputLabel>Applicable categories (empty = all)</InputLabel>
                  <Select
                    multiple
                    label="Applicable categories (empty = all)"
                    {...field}
                    value={field.value || []}
                    renderValue={(sel) =>
                      sel.length ? `${sel.length} categor(y/ies)` : "All categories"
                    }
                  >
                    {categories.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <div className="full-width">
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description (internal note)"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                  />
                )}
              />
            </div>
          </div>

          <div className="coupon-form-footer">
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value !== false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Active"
                  sx={{ margin: 0 }}
                />
              )}
            />

            <CustomButton
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ textTransform: "none", minWidth: 160 }}
            >
              {saving ? "Saving..." : selectedCoupon ? "Update coupon" : "Create coupon"}
            </CustomButton>
          </div>

          <Typography className="coupon-form-note" variant="caption">
            Note: scope a coupon to specific products/categories and the discount
            applies only to those items. Leave both empty to apply to the whole cart.
          </Typography>
        </form>
      )}
      </div>
    </div>
  );
};

export default AddNewCoupon;
